/**
 * Button component — Brand Book §2.1
 * 5 variants: primary, secondary, ghost (tertiary), danger (destructive), upgrade
 * Heights: 32px (compact), 36px (default), 40px (large)
 * 0px border-radius, 13px/600 font, orange focus outline
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { Spinner } from './Spinner';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'tertiary' | 'danger' | 'destructive' | 'upgrade';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      className,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseStyles = clsx(
      'inline-flex items-center justify-center gap-2',
      'font-sans text-body font-semibold',
      'min-w-[80px] transition-[background,border-color] duration-200 ease',
      'disabled:opacity-40 disabled:pointer-events-none',
      'focus:outline-none focus-visible:outline-2 focus-visible:outline-orange focus-visible:outline-offset-2'
    );

    const variants = {
      primary: clsx(
        'bg-orange text-white border-0',
        'hover:bg-orange-hover',
        'active:bg-orange-deep'
      ),
      secondary: clsx(
        'bg-transparent text-txt-primary border border-border',
        'hover:bg-surface-subtle hover:border-border-strong',
        'active:bg-surface-muted'
      ),
      ghost: clsx(
        'bg-transparent text-txt-secondary border-0',
        'hover:bg-surface-subtle',
        'active:bg-surface-muted'
      ),
      danger: clsx(
        'bg-transparent text-error border border-error',
        'hover:bg-error-light',
        'active:bg-[rgba(220,38,38,0.12)]'
      ),
      upgrade: clsx(
        'bg-orange-light text-orange border-0',
        'hover:bg-[rgba(217,131,26,0.12)]',
        'active:bg-[rgba(217,131,26,0.16)]'
      ),
      // Brand Book aliases
      tertiary: clsx(
        'bg-transparent text-txt-secondary border-0',
        'hover:bg-surface-subtle',
        'active:bg-surface-muted'
      ),
      destructive: clsx(
        'bg-transparent text-error border border-error',
        'hover:bg-error-light',
        'active:bg-[rgba(220,38,38,0.12)]'
      ),
    };

    const sizes = {
      sm: 'h-7 px-2.5 text-[11px]',    // 28px small (brand book §2.1)
      md: 'h-9 px-4',                    // 36px default
      lg: 'h-10 px-5 text-[15px]',      // 40px large (brand book §2.1)
    };

    return (
      <button
        ref={ref}
        className={clsx(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {/* Instant acknowledgment is the disabled dim (disabled:opacity-40,
            applied the moment isLoading flips). The spinner itself respects the
            default 1s delay, so sub-second submits show only the dim — no flash. */}
        {isLoading ? (
          <Spinner size="sm" />
        ) : leftIcon ? (
          <span className="flex-shrink-0">{leftIcon}</span>
        ) : null}
        {children}
        {rightIcon && !isLoading && <span className="flex-shrink-0">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';
